// Gets the display name for an activity type, with fallback to formatted activity type
import { ActivityTypeDisplayNames } from '../EngagementDetailsEnums'
import startCase from 'lodash/startCase'
import { ProcessedActivity } from '../../../../api/models'

export type ScrubberActivityBuckets = {
  prerollActivities: ProcessedActivity[]
  engagementActivities: ProcessedActivity[]
  combinedActivityAdjustedDurationInSeconds: number
}

export const DEAD_SPACE_MAX_SECONDS = 15

export const getActivityTypeDisplayName = (activityType: string): string => {
  return (ActivityTypeDisplayNames as Record<string, string>)[activityType] || startCase(activityType)
}

// if the activity is just dead space, then limit it's length to DEAD_SPACE_MAX_SECONDS (15 seconds)
export const getDurationAdjustedForDeadSpaceSeconds = (segment: ProcessedActivity): number =>
  segment.isPrimarilyDeadSpace ? Math.min(DEAD_SPACE_MAX_SECONDS, segment.durationSeconds) : segment.durationSeconds

export const getCombinedActivitiesAdjustedDurationInSeconds = (segments: ProcessedActivity[]): number => {
  return segments.reduce((total, segment) => total + getDurationAdjustedForDeadSpaceSeconds(segment), 0)
}

/**
 * Process and sort activities
 * @param processedActivities
 */
export const groupActivitiesForScrubber = (processedActivities: ProcessedActivity[]): ScrubberActivityBuckets => {
  const processedSegments: ProcessedActivity[] = processedActivities
    .map(processedActivity => processedActivity)
    .sort((a, b) => a.relativeStartTimeSeconds - b.relativeStartTimeSeconds)

  const prerollSegments: ProcessedActivity[] = []
  const postrollSegments: ProcessedActivity[] = []
  for (const segment of processedSegments) {
    // as long as preroll activity ends before the recording (using the adjusted time), we will omit it from the scrubber
    if (segment.relativeEndTimeSeconds <= 0) {
      prerollSegments.push(segment)
    } else {
      postrollSegments.push(segment)
    }
  }

  // Calculate total duration of all activities combined
  const totalAdjustedDuration =
    getCombinedActivitiesAdjustedDurationInSeconds(prerollSegments) + getCombinedActivitiesAdjustedDurationInSeconds(postrollSegments)

  return {
    prerollActivities: prerollSegments,
    engagementActivities: postrollSegments,
    combinedActivityAdjustedDurationInSeconds: totalAdjustedDuration
  }
}

// Helper function to convert mouse position to time using the same logic as ball positioning
export const calculateTimeFromPosition = (
  clickPositionX: number,
  containerWidth: number,
  segments: Array<ProcessedActivity>,
  displayedDurationInSeconds: number
): number => {
  // Bound the click position within container
  const boundedClickX = Math.max(0, Math.min(clickPositionX, containerWidth))

  if (segments.length === 0) {
    // Simple percentage calculation when no segments
    const percentage = boundedClickX / containerWidth
    return percentage * displayedDurationInSeconds
  }

  // Use segment-based calculation (inverse of ball position calculation)
  const percentage = boundedClickX / containerWidth
  let accumulatedRatio = 0

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    const widthDurationSeconds = getDurationAdjustedForDeadSpaceSeconds(segment)
    if (widthDurationSeconds <= 0) continue
    const segmentDurationRatio = widthDurationSeconds / displayedDurationInSeconds

    if (percentage <= accumulatedRatio + segmentDurationRatio) {
      // Click is within this segment
      const segmentProgress = (percentage - accumulatedRatio) / segmentDurationRatio
      return segment.relativeStartTimeSeconds + segmentProgress * segment.durationSeconds
    }

    accumulatedRatio += segmentDurationRatio
  }

  // Click is beyond all segments, return the end of the last segment
  return segments[segments.length - 1].relativeEndTimeSeconds
}
